import { useRef, useState } from 'react';
import { Button } from './Button';
import { Card } from './Card';
import { Icon } from './Icon';
import { Status } from './Status';
import { Stat } from './Stat';
import { Stepper } from './Stepper';
import { ConfirmRunImportModal } from './ConfirmRunImportModal';
import { ConfirmModal } from './ConfirmModal';
import {
  usePreviewImport,
  useRunImport,
  useUploadImport,
  useImport,
} from '@/hooks/useImports';
import type { ImportDetail, ImportSource, PreviewOutput, RecruitmentOrigin } from '@/api/imports';
import { listImportSheets } from '@/api/imports';
import { fmt, fmtBytes, sourceLabel } from '@/lib/format';

type Props = {
  onDone: (importId: number) => void;
};

const SOURCES: Array<{
  value: ImportSource;
  label: string;
  description: string;
  icon: 'dollar' | 'database' | 'call' | 'plug';
}> = [
  { value: 'ORANGE', label: 'Orange Money', description: 'Fichier CSV/Excel quotidien des transactions Mobile Money', icon: 'dollar' },
  { value: 'ORANGE_PROFIL', label: 'Orange — Profil marchands', description: 'Fichier Excel des profils marchands (feuille dataSet) : enrôlements + usage 30 jours', icon: 'database' },
  { value: 'ORANGE_MENSUEL', label: 'Orange — Activité mensuelle', description: "Fichier Excel large : une ligne par marchand, une colonne valeur_AAAA-MM par mois. "
    + "Chaque mois actif devient un résumé de transactions daté.", icon: 'dollar' },
  { value: 'ID30', label: 'PDE.CI', description: 'Extract périodique des fiches entreprenants', icon: 'database' },
  { value: 'CALL_CENTER', label: 'Call center', description: 'Export CSV ou Excel des appels téléopérateurs', icon: 'call' },
  {
    value: 'PARTNER_BATCH',
    label: 'Lot partenaire — format libre',
    description:
      "Fichier dont le format n'est pas connu : vous définirez la correspondance des colonnes à l'étape suivante, et le profil sera réutilisable pour les lots suivants.",
    icon: 'plug',
  },
];

/**
 * Four-step import wizard.
 *
 *   1. Source     : pick the connector type
 *   2. Téléversement : drop or browse a file, get back an import id
 *   3. Mappage + simulation : ask the backend for a preview against the
 *      first 50 rows, render the column mapping and the rejection
 *      breakdown
 *   4. Lancement   : confirm and POST /v1/imports/{id}/run, poll progress
 */
/**
 * Traduit un échec de téléversement.
 *
 * Un `fetch` interrompu lève une TypeError dont le message est « NetworkError
 * when attempting to fetch resource » — la formulation brute du navigateur, qui
 * ne dit ni ce qui a échoué ni quoi faire. Or les causes sont peu nombreuses et
 * identifiables : coupure réseau, fichier refusé par le serveur intermédiaire
 * parce que trop volumineux, ou transfert trop long.
 */
function uploadErrorText(e: unknown, file: File | null): string {
  const raw = e instanceof Error ? e.message : String(e);
  const isNetwork =
    e instanceof TypeError ||
    /NetworkError|Failed to fetch|Load failed|network/i.test(raw);

  if (!isNetwork) return raw;

  const size = file ? ` (${fmtBytes(file.size)})` : '';
  return (
    `Le fichier${size} n'a pas pu être transmis : la connexion a été interrompue. ` +
    `Les causes habituelles sont un fichier trop volumineux pour le serveur intermédiaire, ` +
    `un transfert trop long, ou une coupure réseau. Réessayez ; si l'échec se répète sur ce ` +
    `fichier alors qu'un fichier plus petit passe, c'est la limite de taille du serveur qui ` +
    `est en cause.`
  );
}

export function ImportWizard({ onDone }: Props) {
  const [step, setStep] = useState(1);
  const [source, setSource] = useState<ImportSource>('ORANGE');
  const [importId, setImportId] = useState<number | null>(null);
  const [filename, setFilename] = useState('');
  // Fichier choisi mais PAS encore téléversé : le choix ne déclenche rien,
  // l'upload attend un clic explicite sur « Téléverser ».
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Feuilles du classeur (xlsx multi-feuilles) + celle choisie. Vide quand la
  // source n'a pas de feuille sélectionnable (CSV, feuille unique).
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheet, setSheet] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // Même règle que dans la fiche d'un import : le lancement, qui écrit dans un
  // journal en ajout seul, demande une confirmation explicite.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Le téléversement transmet le fichier au serveur et y crée un import : ça
  // s'annonce, d'autant qu'un gros fichier occupe la ligne plusieurs minutes.
  const [confirmUpload, setConfirmUpload] = useState(false);

  const upload = useUploadImport();
  const previewMutation = usePreviewImport();
  const runMutation = useRunImport();
  const detail = useImport(step === 4 ? importId ?? undefined : undefined);

  const steps = [
    { title: 'Source et type', subtitle: "d'où vient le fichier" },
    { title: 'Téléversement', subtitle: 'déposer le fichier' },
    { title: 'Mappage et simulation', subtitle: 'valider à blanc' },
    { title: 'Lancement', subtitle: 'soumettre le pipeline' },
  ];

  // Choisir un fichier ne fait que le mémoriser (aucun réseau) : l'upload,
  // potentiellement lourd, est déclenché uniquement par startUpload().
  function onFileChosen(file: File) {
    setError(null);
    setSelectedFile(file);
    setFilename(file.name);
    setImportId(null);
    setSheets([]);
    setSheet('');
    setPreview(null);
  }

  async function startUpload() {
    if (!selectedFile) return;
    setConfirmUpload(false);
    setError(null);
    try {
      const res = await upload.mutateAsync({ file: selectedFile, source });
      setImportId(res.id);
      // Plusieurs feuilles ? On laisse choisir (défaut = la 1re), sinon on
      // pré-charge l'aperçu directement — l'étape 3 paraît instantanée.
      const sh = await listImportSheets(res.id);
      const chosen = sh.length > 1 ? sh[0] : '';
      setSheets(sh.length > 1 ? sh : []);
      setSheet(chosen);
      const p = await previewMutation.mutateAsync({
        id: res.id,
        sheet: chosen || undefined,
      });
      setPreview(p ?? null);
    } catch (e: unknown) {
      setError(uploadErrorText(e, selectedFile));
    }
  }

  // Changement de feuille : on recalcule l'aperçu pour la feuille choisie.
  async function handleSheetChange(next: string) {
    if (importId == null || next === sheet) return;
    setSheet(next);
    setError(null);
    setPreview(null);
    try {
      const p = await previewMutation.mutateAsync({ id: importId, sheet: next });
      setPreview(p ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur aperçu');
    }
  }

  async function launch(origin?: {
    recruitmentOrigin: RecruitmentOrigin;
    recruitmentPartner?: string;
  }) {
    if (importId == null) return;
    setError(null);
    setConfirmOpen(false);
    await runMutation.mutateAsync({ id: importId, sheet: sheet || undefined, ...origin });
  }

  // When the run has succeeded (or partial/failed), surface the final
  // state and the "voir l'historique" CTA.
  const finalised =
    detail.data &&
    (detail.data.status === 'SUCCEEDED' ||
      detail.data.status === 'PARTIAL' ||
      detail.data.status === 'FAILED');

  return (
    <div className="fade-in">
      <ConfirmModal
        opened={confirmUpload}
        onClose={() => setConfirmUpload(false)}
        onConfirm={() => void startUpload()}
        title="Téléverser ce fichier ?"
        confirmLabel="Téléverser"
        loading={upload.isPending}
      >
        <div style={{ fontFamily: 'var(--mono)', fontWeight: 700 }}>{filename}</div>
        <div style={{ marginTop: 4 }}>
          {selectedFile ? fmtBytes(selectedFile.size) : ''} · source {sourceLabel(source)}
        </div>
        <p style={{ marginBottom: 0 }}>
          Le fichier sera transmis au serveur et enregistré comme import <strong>en attente</strong>.
          Aucune donnée n'est ingérée à cette étape : l'import ne part qu'au lancement, qui se
          confirme séparément.
        </p>
      </ConfirmModal>

      <ConfirmRunImportModal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        running={runMutation.isPending}
        filename={filename}
        source={source}
        sheet={sheet}
        preview={preview}
        onConfirm={(origin) => void launch(origin)}
      />

      <Card pad>
        <Stepper steps={steps} current={step} />

        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 24 }}>
          {step === 1 && (
            <div className="grid grid-2">
              {SOURCES.map((s) => {
                const selected = source === s.value;
                return (
                  <div
                    key={s.value}
                    onClick={() => setSource(s.value)}
                    style={{
                      cursor: 'pointer',
                      padding: 18,
                      borderRadius: 14,
                      border: `1px solid ${selected ? 'var(--accent-tint-2)' : 'var(--line)'}`,
                      background: selected ? 'var(--accent-tint)' : 'var(--panel)',
                      transition: '.14s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div
                        className="tl-node"
                        style={
                          selected
                            ? { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' }
                            : undefined
                        }
                      >
                        <Icon name={s.icon} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 15 }}>{s.label}</div>
                        <div className="muted" style={{ fontSize: 12.5, fontWeight: 600 }}>
                          parser dédié côté backend
                        </div>
                      </div>
                      {selected && (
                        <span style={{ marginLeft: 'auto', color: 'var(--accent-2)' }}>
                          <Icon name="check" />
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 12, fontWeight: 500 }}>
                      {s.description}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {step === 2 && (
            <div>
              <div
                className="dropzone"
                onClick={() => {
                  if (!upload.isPending) inputRef.current?.click();
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (upload.isPending) return;
                  const f = e.dataTransfer.files?.[0];
                  if (f) onFileChosen(f);
                }}
              >
                <div className="di">
                  {upload.isPending ? <span className="spin" /> : <Icon name="upload" />}
                </div>
                <div style={{ fontWeight: 800, fontSize: 15, marginTop: 6 }}>
                  {filename || 'Déposer un fichier ici, ou cliquer pour parcourir'}
                </div>
                <div className="muted" style={{ fontSize: 13, fontWeight: 500 }}>
                  CSV, Excel ou JSON, jusqu'à 200 Mo
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept=".csv,.tsv,.xlsx,.xls,.json"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onFileChosen(f);
                  }}
                />
              </div>

              {/* Fichier choisi mais pas encore envoyé → confirmation explicite. */}
              {selectedFile && importId == null && (
                <div
                  style={{
                    marginTop: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                  }}
                >
                  <span className="meta">
                    <span className="sq" />
                    {filename} · {fmtBytes(selectedFile.size)}
                  </span>
                  <Button
                    variant="primary"
                    icon="upload"
                    disabled={upload.isPending || previewMutation.isPending}
                    onClick={() => setConfirmUpload(true)}
                  >
                    {upload.isPending ? 'Téléversement…' : 'Téléverser le fichier'}
                  </Button>
                </div>
              )}
              {importId != null && (
                <div className="meta acc" style={{ marginTop: 16 }}>
                  <span className="sq" />
                  Import #{importId} créé · attente de l'aperçu
                </div>
              )}
              {sheets.length > 1 && (
                <div
                  style={{
                    marginTop: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 13.5 }}>
                    <Icon name="database" size={16} style={{ color: 'var(--accent-2)' }} />
                    Feuille à importer
                  </span>
                  <select
                    value={sheet}
                    onChange={(e) => void handleSheetChange(e.target.value)}
                    disabled={previewMutation.isPending}
                    style={{
                      padding: '9px 12px',
                      borderRadius: 10,
                      border: '1px solid var(--line)',
                      background: 'var(--panel)',
                      color: 'var(--ink)',
                      fontWeight: 600,
                      fontSize: 13.5,
                    }}
                  >
                    {sheets.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>
                    {sheets.length} feuilles détectées{previewMutation.isPending ? ' · aperçu…' : ''}
                  </span>
                </div>
              )}
              {error && <div className="err-msg" style={{ marginTop: 14 }}>{error}</div>}
            </div>
          )}

          {step === 3 && preview && <PreviewSummary preview={preview} loading={previewMutation.isPending} />}
          {step === 3 && !preview && (
            <div style={{ textAlign: 'center', padding: 30 }}>
              {previewMutation.isPending ? (
                <span className="muted">Calcul de l’aperçu…</span>
              ) : error ? (
                <div className="err-msg" style={{ textAlign: 'left' }}>
                  Aperçu impossible : {error}
                </div>
              ) : (
                <span className="muted">Aucun aperçu disponible.</span>
              )}
            </div>
          )}

          {step === 4 && (
            <RunStep
              detail={detail.data ?? null}
              filename={filename}
              source={source}
              onLaunch={() => setConfirmOpen(true)}
              running={runMutation.isPending || (detail.data?.status === 'RUNNING')}
              finalised={finalised ?? false}
              onSeeHistory={() => importId != null && onDone(importId)}
              error={error}
            />
          )}
        </div>

        {!finalised && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              marginTop: 28,
              paddingTop: 20,
              borderTop: '1px solid var(--line)',
            }}
          >
            <Button icon="arrowleft" disabled={step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))}>
              Précédent
            </Button>
            {step < 4 && (
              <Button
                variant="primary"
                iconRight="chevright"
                disabled={
                  (step === 2 && importId == null) ||
                  (step === 3 && !preview)
                }
                onClick={() => setStep((s) => Math.min(4, s + 1))}
              >
                Continuer
              </Button>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function PreviewSummary({ preview, loading }: { preview: PreviewOutput; loading: boolean }) {
  const acceptance = preview.sampleSize > 0 ? Math.round((preview.accepted / preview.sampleSize) * 1000) / 10 : 0;
  const reasons = Object.entries(preview.rejectionReasons ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 14 }}>Mappage des colonnes vers les champs canoniques</div>
      <Card className="scrollx" style={{ marginBottom: 22 }}>
        <table className="tbl">
          <thead>
            <tr><th>Colonne du fichier</th><th>Champ canonique</th><th>Détection</th></tr>
          </thead>
          <tbody>
            {Object.entries(preview.columnMapping ?? {}).map(([col, canonical]) => (
              <tr key={col}>
                <td style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--ink)' }}>{col}</td>
                <td><span className="code">{canonical || 'non mappé'}</span></td>
                <td>
                  {canonical ? (
                    <Status kind="ok">Automatique</Status>
                  ) : (
                    <Status kind="warn">À ajuster</Status>
                  )}
                </td>
              </tr>
            ))}
            {Object.keys(preview.columnMapping ?? {}).length === 0 && (
              <tr><td colSpan={3} className="muted" style={{ padding: 18, textAlign: 'center' }}>Aucune colonne mappée par le parser</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="play" size={16} style={{ color: 'var(--accent-2)' }} />
        Validation à blanc {loading ? '(calcul…)' : `sur ${fmt(preview.sampleSize)} lignes`}
      </div>
      <div className="grid grid-4">
        <Stat label="Lignes simulées" value={fmt(preview.sampleSize)} />
        <Stat label="Acceptées" value={fmt(preview.accepted)} trend="up" />
        <Stat label="Rejetées" value={fmt(preview.rejected)} trend={preview.rejected > 0 ? 'down' : undefined} />
        <Stat label="Taux d'acceptation" value={`${acceptance.toLocaleString('fr-FR')} %`} tint />
      </div>

      <Card pad style={{ marginTop: 18 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Causes de rejet anticipées</div>
        {reasons.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
            Aucun rejet anticipé sur cet échantillon.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {reasons.map(([code, n]) => (
              <div key={code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="meta alert"><span className="sq" />{code}</span>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{n} ligne{n > 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function RunStep({
  detail,
  filename,
  source,
  onLaunch,
  running,
  finalised,
  onSeeHistory,
  error,
}: {
  detail: ImportDetail | null;
  filename: string;
  source: ImportSource;
  onLaunch: () => void;
  running: boolean;
  finalised: boolean;
  onSeeHistory: () => void;
  error: string | null;
}) {
  if (finalised && detail) {
    const ratio =
      detail.itemsTotal > 0 ? Math.round((detail.itemsAccepted / detail.itemsTotal) * 1000) / 10 : 0;
    return (
      <div style={{ textAlign: 'center', padding: '6px 0' }}>
        <div className="tl-node" style={{ width: 56, height: 56, margin: '0 auto 16px', background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' }}>
          <Icon name="check" size={26} />
        </div>
        <div style={{ fontWeight: 800, fontSize: 19, letterSpacing: '-.02em' }}>
          Import {detail.status === 'SUCCEEDED' ? 'terminé' : detail.status === 'PARTIAL' ? 'partiel' : 'en échec'}
        </div>
        <div className="muted" style={{ fontSize: 13.5, fontWeight: 500, marginTop: 5 }}>
          Enregistré dans import_log et soumis à la plateforme.
        </div>
        <div className="grid grid-4" style={{ marginTop: 24, textAlign: 'left' }}>
          <Stat label="Total" value={fmt(detail.itemsTotal)} />
          <Stat label="Acceptés" value={fmt(detail.itemsAccepted)} trend="up" />
          <Stat label="Rejetés" value={fmt(detail.itemsRejected)} trend={detail.itemsRejected > 0 ? 'down' : undefined} />
          <Stat label="Acceptance" value={`${ratio.toLocaleString('fr-FR')} %`} tint />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24 }}>
          <Button icon="history" onClick={onSeeHistory}>Voir dans l'historique</Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '10px 0' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <dl className="kv" style={{ textAlign: 'left', marginBottom: 24 }}>
          <dt>Source</dt><dd>{source}</dd>
          <dt>Fichier</dt><dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{filename || '—'}</dd>
          <dt>Status courant</dt><dd>{detail?.status ?? 'PENDING'}</dd>
          <dt>Mode de soumission</dt><dd>lots de 500 via le pipeline interne</dd>
        </dl>
        {running ? (
          <div>
            <div className="progress" style={{ marginBottom: 12 }}>
              <div className="fill" style={{ width: progressPercent(detail) + '%' }} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              {fmt(detail?.itemsAccepted ?? 0)} / {fmt(detail?.itemsTotal ?? 0)} commités
            </div>
          </div>
        ) : (
          <Button
            variant="primary"
            icon="play"
            onClick={onLaunch}
            style={{ padding: '13px 28px', fontSize: 14 }}
          >
            Lancer l'import
          </Button>
        )}
        {error && <div className="err-msg" style={{ marginTop: 14 }}>{error}</div>}
      </div>
    </div>
  );
}

function progressPercent(detail: ImportDetail | null): number {
  if (!detail || detail.itemsTotal === 0) return 0;
  return Math.min(100, Math.round((detail.itemsAccepted + detail.itemsRejected) / detail.itemsTotal * 100));
}
